import { Box, Button, Grid } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AsyncSelectScroll from "../../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import Flex from "../../../../../../components/Flex";
import StyledDialog from "../../../../../../components/StyledDialog";
import Text from "../../../../../../components/Text";
import ValueSwitcher from "../../../../../../components/ValueSwitcher";
import { Option } from "../../../../../../constants/common";
import { PROVIDER_PATHS } from "../../../../../../constants/paths";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../hooks/use-disclosure";
import { useGetClaimsV2 } from "../../../../../../queries/billings/claims/use-get-claims-v2";
import { formatDate } from "../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
} from "../../../../../../utils/utils";

interface CopyClaimModalProps {
  disclosureHost: DisclosureHost;
}

const VISIT_ID_OPTIONS: Option[] = [
  {
    value: "SAME",
    label: "Same Visit ID",
  },
  {
    value: "NEW",
    label: "New Visit ID",
  },
];

type FormikValues = {
  visitId: "SAME" | "NEW";
  dateOfService: Date | undefined;
  claimId: { value: string; patientId: string } | undefined;
};
const INITIAL_VALUES: FormikValues = {
  visitId: "SAME",
  dateOfService: undefined,
  claimId: undefined,
};

const CopyClaimModal = ({ disclosureHost }: CopyClaimModalProps) => {
  // const [_, setSearchParams] = useSearchParams();
  const [claimSearchKey, setClaimSearchKey] = useDebounce<string>("");
  const [claimPagination, setClaimPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const closeModal = () => {
    disclosureHost.close();
  };

  //TODO: need light weight api call to get claims list
  const {
    data: claimsList,
    isLoading: claimsDataLoading,
    isFetching: isClaimFetching,
  } = useGetClaimsV2({
    gridSorting: [],
    pagination: claimPagination,
    searchKey: claimSearchKey ?? undefined,
  });

  const claimOptions = claimsList?.data?.content?.map((claim) => ({
    value: claim.uuid,
    dateOfService: `${
      claim?.dateOfService ? formatDate(claim?.dateOfService) : ""
    }`,
    label: `${claim?.patientName} - ${getFormattedId(claim?.uuid)}`,
    patientId: claim?.patientId ?? "",
  }));

  const navigate = useNavigate();
  const formik = useFormik<FormikValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: Yup.object({
      claimId: Yup.object().required("Claim ID is required"),
    }),
    onSubmit: (values) => {
      navigate(
        PROVIDER_PATHS.BILLING +
          `claims/edit-claim?patientId=${values.claimId?.patientId ?? ""}&claimStatus=COPY&claimId=${values.claimId?.value ?? ""}&dateOfService=${values.dateOfService?.toISOString() ?? ""}`
      );
      // navigate("claims/edit-claim");
      // setSearchParams({
      //   patientId: values.claimId?.patientId ?? "",
      //   claimId: values.claimId?.value ?? "",
      //   claimStatus: "COPY",
      //   dateOfService: values.dateOfService?.toISOString() ?? "",
      // });
    },
  });

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Copy From Existing Claim"
      dialogAction={
        <Button variant="contained" color="primary" onClick={formik.submitForm}>
          Copy
        </Button>
      }
    >
      <Box width={"38vw"} height={"14vh"}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Flex alignItems={"center"} gap={1} mb={2}>
              <Text>Visit ID:</Text>

              <ValueSwitcher
                alignment={formik.values.visitId}
                handleChange={(_, value) => {
                  formik.setFieldValue("visitId", value as "SAME" | "NEW");
                }}
                timing={VISIT_ID_OPTIONS}
              />
            </Flex>
          </Grid>

          {/*fields only Visible On New Visit ID */}
          {formik.values.visitId === "NEW" && (
            <Grid item xs={6}>
              <CustomDatePicker
                label="Date of Service"
                maxDate={dayjs(new Date())}
                value={formik.values.dateOfService}
                onChange={(value) =>
                  formik.setFieldValue("dateOfService", value)
                }
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <AsyncSelectScroll
              onSearch={(value) => {
                setClaimSearchKey(value);
                setClaimPagination((p) => ({ ...p, page: 0 }));
              }}
              loading={isClaimFetching || claimsDataLoading}
              pagination={claimPagination}
              setPagination={setClaimPagination}
              totalElements={claimsList?.data?.page?.totalElements}
              label="Claim ID"
              options={claimOptions as any}
              renderCustomLabel={(option) => (
                <Flex flexDirection={"column"} gap={1}>
                  <Text>{option.label}</Text>
                  <Text>{option.dateOfService}</Text>
                </Flex>
              )}
              placeholder="Search And Select ID"
              value={formik.values.claimId}
              onChange={(value) => formik.setFieldValue("claimId", value)}
            />
          </Grid>
        </Grid>
      </Box>
    </StyledDialog>
  );
};

export default CopyClaimModal;
